/**
 * AdminIncidents — single audit log of every alert + circuit-breaker trip.
 *
 * Each row shows when, source, severity, title, and the email body that
 * went out. Operators click "Resolve" with an optional note to clear it.
 * Resolved incidents stay visible (filter toggle) so you have a real
 * history of what's broken in your app and how it was fixed.
 *
 * Also surfaces the "circuit breaker" state per worker — if a worker has
 * auto-disabled itself, the page shows a "Reset circuit" button next to
 * its name so the admin can re-enable without a deploy.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import {
  AlertCircle, AlertTriangle, BellOff, CheckCircle2, ChevronDown, ChevronRight,
  CircuitBoard, Clock, Info, Loader2, RefreshCw, RotateCcw, Volume2, VolumeX, XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/apiBase';
import useHideCookieBanner from '../utils/useHideCookieBanner';
import AdminReadinessBanner from '../components/AdminReadinessBanner';
import AdminSubNav from '../components/AdminSubNav';


const SEVERITY_TONE = {
  fail: { bg: '#fef2f2', fg: '#991b1b', label: 'CRITICAL', Icon: XCircle },
  warn: { bg: '#fffbeb', fg: '#92400e', label: 'WARNING', Icon: AlertTriangle },
  info: { bg: '#ecfdf5', fg: '#065f46', label: 'INFO', Icon: Info },
};


const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_e) {
    return iso;
  }
};


const AdminIncidents = () => {
  const { user, loading: authLoading } = useAuth();
  useHideCookieBanner();
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [resolveNotes, setResolveNotes] = useState({});
  const [snooze, setSnooze] = useState({ active: false });
  const [snoozeReason, setSnoozeReason] = useState('');
  const [snoozeBusy, setSnoozeBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const loadSnooze = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/snooze`, { withCredentials: true });
      setSnooze(r.data || { active: false });
    } catch (_e) { /* noop */ }
  }, []);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true); else setRefreshing(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter) params.set('status', statusFilter);
      const r = await axios.get(`${API}/admin/incidents?${params}`, { withCredentials: true });
      setData(r.data);
    } catch (_e) {
      // soft-fail
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(true); loadSnooze(); }, [load, loadSnooze]);

  // Live countdown for the snooze banner — ticks every 1s while a
  // snooze is active so the "expires in 1h 23m" text stays accurate.
  useEffect(() => {
    if (!snooze?.active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [snooze?.active]);

  const handleSnooze = async (minutes) => {
    setSnoozeBusy(true);
    try {
      await axios.post(`${API}/admin/snooze`, {
        minutes,
        reason: snoozeReason || 'manual',
      }, { withCredentials: true });
      setSnoozeReason('');
      await loadSnooze();
      load(false);
    } catch (_e) { /* noop */ } finally { setSnoozeBusy(false); }
  };

  const handleClearSnooze = async () => {
    setSnoozeBusy(true);
    try {
      await axios.delete(`${API}/admin/snooze`, { withCredentials: true });
      await loadSnooze();
      load(false);
    } catch (_e) { /* noop */ } finally { setSnoozeBusy(false); }
  };

  const handleResolve = async (incident_id) => {
    try {
      const note = resolveNotes[incident_id] || '';
      await axios.post(`${API}/admin/incidents/${incident_id}/resolve`, { note }, { withCredentials: true });
      setResolveNotes((s) => ({ ...s, [incident_id]: '' }));
      setExpanded(null);
      load(false);
    } catch (_e) { /* noop */ }
  };

  const handleReopen = async (incident_id) => {
    try {
      await axios.post(`${API}/admin/incidents/${incident_id}/reopen`, {}, { withCredentials: true });
      load(false);
    } catch (_e) { /* noop */ }
  };

  const handleResetCircuit = async (worker_name) => {
    if (!window.confirm(`Reset the circuit breaker for ${worker_name}? The worker will resume polling.`)) return;
    try {
      await axios.post(`${API}/admin/incidents/circuit/${worker_name}/reset`, {}, { withCredentials: true });
      load(false);
    } catch (_e) { /* noop */ }
  };

  const breakerRows = useMemo(() => {
    if (!data?.circuit_breakers) return [];
    return Object.values(data.circuit_breakers);
  }, [data]);

  const trippedBreakers = breakerRows.filter((b) => b.tripped);

  if (authLoading) return <div className="pt-24 text-center text-[#6b7280]">Loading…</div>;
  if (!user || user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20 pb-12" data-testid="admin-incidents-page">
      <AdminReadinessBanner />
      <AdminSubNav />

      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7]">ADMIN · INCIDENTS</span>
            <h1 className="font-satoshi text-3xl font-black text-[#0f1115] flex items-center gap-2">
              <AlertCircle size={22} className={data?.open_count > 0 ? 'text-[#e11d48]' : 'text-[#10b981]'} />
              Incidents
            </h1>
            <p className="text-sm text-[#6b7280]">
              Audit trail of every alert + circuit-breaker trip · {data?.open_count || 0} currently open
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 bg-white border border-[#e5e7eb] p-1">
              {[{ k: 'open', label: 'Open' }, { k: 'resolved', label: 'Resolved' }, { k: '', label: 'All' }].map((opt) => (
                <button
                  key={opt.k || 'all'}
                  onClick={() => setStatusFilter(opt.k)}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    statusFilter === opt.k ? 'bg-[#0f1115] text-white' : 'text-[#4b5563] hover:bg-[#f4f5f7]'
                  }`}
                  data-testid={`incidents-filter-${opt.k || 'all'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => load(false)}
              className="px-3 py-2 text-sm font-semibold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] transition-colors inline-flex items-center gap-1.5"
              data-testid="incidents-refresh"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Updating…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Snooze widget — global maintenance window. When active, ALL
            outbound alert emails are suppressed (audit log still records). */}
        <section className="mb-6" data-testid="snooze-section">
          {snooze?.active && snooze.snooze ? (
            <div className="bg-[#fffbeb] border-l-4 border-[#f59e0b] p-4 flex items-start gap-3" data-testid="snooze-active-banner">
              <BellOff size={18} className="text-[#92400e] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-sm text-[#92400e] flex items-center gap-2">
                  Alerts are snoozed
                  {(() => {
                    try {
                      const expiresMs = new Date(snooze.snooze.expires_at).getTime();
                      const remainingMs = Math.max(0, expiresMs - now);
                      const mins = Math.floor(remainingMs / 60000);
                      const secs = Math.floor((remainingMs % 60000) / 1000);
                      const hrs = Math.floor(mins / 60);
                      const remMins = mins % 60;
                      return (
                        <span className="font-mono text-xs bg-[#0f1115] text-[#fbbf24] px-2 py-0.5">
                          {hrs > 0 ? `${hrs}h ${remMins}m` : `${remMins}m ${secs}s`} remaining
                        </span>
                      );
                    } catch (_e) { return null; }
                  })()}
                </div>
                <div className="text-xs text-[#7c2d12] mt-1">
                  Reason: <em>{snooze.snooze.reason || 'manual'}</em> · set by{' '}
                  <code className="font-mono">{snooze.snooze.set_by}</code> at{' '}
                  {fmtWhen(snooze.snooze.set_at)}
                </div>
              </div>
              <button
                onClick={handleClearSnooze}
                disabled={snoozeBusy}
                className="inline-flex items-center gap-1.5 bg-[#0f1115] text-white px-3 py-1.5 text-xs font-bold hover:bg-[#002fa7] disabled:opacity-50"
                data-testid="snooze-clear-btn"
              >
                <Volume2 size={12} /> Resume alerts now
              </button>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e7eb] p-4" data-testid="snooze-inactive-panel">
              <div className="flex items-start gap-3 mb-3">
                <VolumeX size={18} className="text-[#6b7280] flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-bold text-sm text-[#0f1115]">Snooze all alerts</div>
                  <div className="text-xs text-[#6b7280] mt-0.5">
                    Silences every outgoing alert (stripe / readiness / errors / circuit-breaker) for a bounded window.
                    Max {snooze?.max_minutes || 240} minutes. Audit log still records what fires.
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  placeholder="Reason (e.g. 'deploying v2.3', optional)"
                  value={snoozeReason}
                  onChange={(e) => setSnoozeReason(e.target.value)}
                  className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-[#e5e7eb] outline-none focus:border-[#002fa7]"
                  data-testid="snooze-reason-input"
                />
                {[15, 30, 60, 120, 240].map((m) => (
                  <button
                    key={m}
                    onClick={() => handleSnooze(m)}
                    disabled={snoozeBusy}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-white border border-[#e5e7eb] text-[#4b5563] hover:border-[#002fa7] disabled:opacity-50"
                    data-testid={`snooze-btn-${m}`}
                  >
                    <Clock size={11} /> {m >= 60 ? `${m / 60}h` : `${m}m`}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Circuit breakers — only render if any have tripped */}
        {trippedBreakers.length > 0 && (
          <section className="mb-6" data-testid="tripped-breakers">
            <div className="bg-[#fef2f2] border-l-4 border-[#e11d48] p-4">
              <div className="flex items-center gap-2 mb-2">
                <CircuitBoard size={16} className="text-[#e11d48]" />
                <span className="font-bold text-sm text-[#991b1b]">
                  {trippedBreakers.length} worker(s) auto-disabled
                </span>
              </div>
              <ul className="space-y-1.5">
                {trippedBreakers.map((b) => (
                  <li key={b.name} className="flex items-center justify-between gap-3 text-sm" data-testid={`breaker-${b.name}`}>
                    <span className="font-mono">{b.name}</span>
                    <button
                      onClick={() => handleResetCircuit(b.name)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold bg-[#0f1115] text-white hover:bg-[#002fa7]"
                      data-testid={`reset-circuit-${b.name}`}
                    >
                      <RotateCcw size={11} /> Reset circuit
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Incident list */}
        {loading ? (
          <div className="bg-white border border-[#e5e7eb] p-8 text-center">
            <Loader2 size={24} className="animate-spin mx-auto text-[#6b7280]" />
          </div>
        ) : !data?.incidents || data.incidents.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] p-8 text-center" data-testid="incidents-empty">
            <CheckCircle2 size={32} className="text-[#10b981] mx-auto mb-2" />
            <p className="text-sm text-[#6b7280]">
              {statusFilter === 'open' ? 'No open incidents. All systems are quiet.' : 'No incidents in this view.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#e5e7eb] divide-y divide-[#e5e7eb]" data-testid="incidents-list">
            {data.incidents.map((inc) => {
              const tone = SEVERITY_TONE[inc.severity] || SEVERITY_TONE.warn;
              const isExpanded = expanded === inc.incident_id;
              const isOpen = inc.status === 'open';
              return (
                <div key={inc.incident_id} data-testid={`incident-${inc.incident_id}`}>
                  <button
                    type="button"
                    onClick={() => setExpanded(isExpanded ? null : inc.incident_id)}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#f4f5f7] text-left"
                  >
                    {isExpanded ? <ChevronDown size={14} className="text-[#6b7280]" /> : <ChevronRight size={14} className="text-[#6b7280]" />}
                    <span className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 flex-shrink-0" style={{ backgroundColor: tone.bg, color: tone.fg }}>
                      {tone.label}
                    </span>
                    <span className="text-xs font-mono text-[#6b7280] flex-shrink-0">{inc.source}</span>
                    <span className="font-semibold text-sm text-[#0f1115] flex-1 truncate">{inc.title}</span>
                    {isOpen ? (
                      <span className="text-[10px] font-bold text-[#e11d48] flex-shrink-0">OPEN</span>
                    ) : (
                      <span className="text-[10px] font-bold text-[#9ca3af] flex-shrink-0">RESOLVED</span>
                    )}
                    <span className="text-[11px] text-[#9ca3af] flex-shrink-0 whitespace-nowrap">{fmtWhen(inc.created_at)}</span>
                  </button>
                  {isExpanded && (
                    <div className="px-4 py-4 bg-[#f4f5f7]" data-testid={`incident-body-${inc.incident_id}`}>
                      <div className="bg-white border border-[#e5e7eb] p-4 mb-3 max-h-96 overflow-y-auto">
                        <div dangerouslySetInnerHTML={{ __html: inc.body || '<p class="text-[#9ca3af]">(no body)</p>' }} />
                      </div>
                      {!isOpen && inc.resolution_note && (
                        <div className="bg-[#ecfdf5] border-l-4 border-[#10b981] p-3 mb-3 text-sm">
                          <div className="text-[10px] font-bold text-[#065f46] uppercase tracking-wider mb-1">
                            Resolution note · {inc.resolved_by || 'unknown'} · {fmtWhen(inc.resolved_at)}
                          </div>
                          <div className="text-[#374151]">{inc.resolution_note}</div>
                        </div>
                      )}
                      {isOpen ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="text"
                            placeholder="Optional resolution note (what did you do?)"
                            value={resolveNotes[inc.incident_id] || ''}
                            onChange={(e) => setResolveNotes((s) => ({ ...s, [inc.incident_id]: e.target.value }))}
                            className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-[#e5e7eb] outline-none focus:border-[#002fa7]"
                            data-testid={`incident-note-${inc.incident_id}`}
                          />
                          <button
                            onClick={() => handleResolve(inc.incident_id)}
                            className="inline-flex items-center gap-1.5 bg-[#10b981] text-white px-3 py-1.5 text-sm font-bold hover:bg-[#059669]"
                            data-testid={`incident-resolve-${inc.incident_id}`}
                          >
                            <CheckCircle2 size={14} /> Resolve
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleReopen(inc.incident_id)}
                          className="inline-flex items-center gap-1.5 bg-white border border-[#e5e7eb] text-[#4b5563] px-3 py-1.5 text-sm font-bold hover:border-[#e11d48] hover:text-[#e11d48]"
                          data-testid={`incident-reopen-${inc.incident_id}`}
                        >
                          <RotateCcw size={14} /> Re-open
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminIncidents;
